import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { AuthCard } from '@/components/marketing/auth-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { auth } from '@/lib/auth';

/** Only same-site paths may be returned to after signing in. */
const safeRedirect = (r: unknown): string | undefined =>
  typeof r === 'string' && r.startsWith('/') && !r.startsWith('//') ? r : undefined;

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => {
    const redirect = safeRedirect(s['redirect']);
    return redirect ? { redirect } : {};
  },
});

function LoginPage() {
  const { redirect = '/dashboard' } = Route.useSearch();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [signedIn, setSignedIn] = useState(false);

  // Someone already signed in gets a link straight in.
  useEffect(() => {
    void auth.getUser().then((u) => setSignedIn(Boolean(u)));
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!auth.enabled) {
      void navigate({ href: redirect });
      return;
    }
    setBusy(true);
    void auth.signIn(redirect, { email: email.trim() }).catch(() => setBusy(false));
  };

  return (
    <AuthCard
      title="Log in to continue"
      footer={
        <span className="text-brand-slate">
          New to Yedo Books?{' '}
          <Link to="/signup" className="font-medium text-primary hover:underline">
            Create an account
          </Link>
        </span>
      }
    >
      {signedIn ? (
        <div className="mb-5 rounded-md bg-brand-blue-50 px-4 py-3 text-sm">
          You are already signed in.{' '}
          <a href={redirect} className="font-semibold text-primary hover:underline">
            Open Yedo Books
          </a>
        </div>
      ) : null}
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="text-xs font-semibold text-brand-slate">Email</span>
          <Input
            type="email"
            autoComplete="email"
            autoFocus
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 h-10 bg-white"
            data-testid="login-email"
          />
        </label>
        <Button type="submit" className="h-10 w-full font-semibold" disabled={busy}>
          {busy ? 'Opening sign-in…' : 'Continue'}
        </Button>
        <p className="text-center text-xs text-brand-slate">
          You will enter your password on the secure sign-in page next.
        </p>
      </form>
    </AuthCard>
  );
}
